import React, {useCallback, useEffect, useRef, useState} from 'react';
import {getZones, zoneMarker} from "../../api/app";
import {GoogleMap, InfoWindow, Marker, OverlayView, Polygon, useLoadScript} from "@react-google-maps/api";
import mapStyle from "../../data/map.json";
import {capitalize} from "../../utils/capitalize";

let data = [];
let coordinate = null;

const GMap = ({mainZone, polygons, data, filters}) => {
    const mapRef = useRef(null);
    const defaultCenter = { lat: -15.4510304, lng: 28.4576441 }; // Set a default center
    const defaultZoom = 10; // Set a default zoom level

    const [activeMarker, setActiveMarker] = useState(null);

    const onLoad = useCallback(map => {
        mapRef.current = map;

        if ((polygons && polygons.length > 0) || mainZone) {
            const bounds = new window.google.maps.LatLngBounds();

            if (mainZone && mainZone.coordinates.length > 0) {
                mainZone.coordinates.forEach(coord => bounds.extend(coord));
            }

            polygons.forEach(polygon => {
                polygon.coordinates.forEach(coord => {
                    bounds.extend(coord);
                });
            });

            map.fitBounds(bounds);
        } else {
            map.setCenter(defaultCenter);
            map.setZoom(defaultZoom);
        }
    }, [mainZone, polygons]);

    useEffect(() => {
        if (mapRef.current && ((polygons && polygons.length > 0) || mainZone)) {
            const bounds = new window.google.maps.LatLngBounds();

            if (mainZone && mainZone.coordinates.length > 0) {
                mainZone.coordinates.forEach(coord => bounds.extend(coord));
            }

            polygons.forEach(polygon => {
                polygon.coordinates.forEach(coord => {
                    bounds.extend(coord);
                });
            });

            mapRef.current.fitBounds(bounds);
        } else if (mapRef.current) {
            mapRef.current.setCenter(defaultCenter);
            mapRef.current.setZoom(defaultZoom);
        }
    }, [mainZone, polygons]);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_KEY,
        libraries: ["drawing", "geometry"]
    });

    const calculateCenter = (coordinates) => {
        const bounds = new window.google.maps.LatLngBounds();
        coordinates.forEach(coord => bounds.extend(coord));
        return bounds.getCenter().toJSON();
    };

    return (
        <>
            {!isLoaded ? (
                <h6>Loading Map...</h6>
            ) : (
                <div>
                    <GoogleMap
                        mapContainerClassName="_map-container"
                        onLoad={onLoad}
                        options={{ styles: mapStyle }}
                    >
                        {mainZone && (
                            <Polygon
                                paths={mainZone.coordinates}
                                options={{
                                    fillColor: mainZone.fillColor || "#FD2311",
                                    fillOpacity: 0, // No fill
                                    strokeColor: "#FD2311",
                                    strokeOpacity: 0.6,
                                    strokeWeight: 2.5,
                                    editable: false,
                                    draggable: false
                                }}
                            />
                        )}

                        {polygons && polygons.length > 0 && polygons.map((polygon, index) => (
                            <React.Fragment key={index}>
                                <Polygon
                                    paths={polygon.coordinates}
                                    options={{
                                        fillColor: polygon.fillColor,
                                        fillOpacity: 0.35,
                                        strokeColor: "#FF0000",
                                        strokeOpacity: 0,
                                        strokeWeight: 2.5,
                                        editable: false,
                                        draggable: false
                                    }}
                                />
                                <OverlayView
                                    position={calculateCenter(polygon.coordinates)}
                                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                >
                                    <div style={{
                                        display: 'inline-block',
                                        backgroundColor: 'white',
                                        padding: '5px 10px',
                                        borderRadius: '8px',
                                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
                                        textAlign: 'center',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        <h6 style={{
                                            margin: 0,
                                            padding: 0,
                                            fontSize: '14px'
                                        }}>
                                            {polygon.name}
                                        </h6>
                                    </div>
                                </OverlayView>
                            </React.Fragment>
                        ))}

                        {data?.map((user, index) => {

                            let coords = null;
                            const isInsideZone = (coordsArray) => {
                                if (!mainZone?.coordinates?.length) return false;
                                const point = new window.google.maps.LatLng(
                                    coordsArray[0],
                                    coordsArray[1]
                                );

                                const polygon = new window.google.maps.Polygon({
                                    paths: mainZone.coordinates
                                });

                                return window.google.maps.geometry.poly.containsLocation(
                                    point,
                                    polygon
                                );
                            };

                            if (user.subscription?.address?.location?.coordinates) {
                                const subCoords = user.subscription.address.location.coordinates;

                                if (subCoords.length === 2 && isInsideZone(subCoords)) {
                                    coords = subCoords;
                                }
                            }

                            if (!coords && user.addresses?.length > 0) {
                                const userCoords = user.addresses[0]?.location?.coordinates;

                                if (userCoords?.length === 2 && isInsideZone(userCoords)) {
                                    coords = userCoords;
                                }
                            }

                            if (!coords) return null;

                            const position = {
                                lat: coords[0],
                                lng: coords[1]
                            };

                            const status = user.subscription?.status || null;

                            let color = "#E53935";
                            let type = "hollow"; // default unenrolled

                            if (status === "paid") {
                                color = "#1BAF5D";
                                type = "active";
                            } else if (status === "credit") {
                                color = "#FBC02D";
                                type = "credit";
                            } else if (!user.subscription) {
                                type = "hollow";
                            }

                            if (!filters?.[type]) return null;

                            return (
                                <React.Fragment key={index}>
                                    <Marker
                                        position={position}
                                        onClick={() => setActiveMarker(index)}
                                        icon={{
                                            path: window.google.maps.SymbolPath.CIRCLE,
                                            scale: 7,
                                            fillColor: color,
                                            fillOpacity: 1,
                                            strokeWeight: 1,
                                            strokeColor: "#ffffff"
                                        }}
                                    />

                                    {activeMarker === index && (
                                        <OverlayView
                                            position={position}
                                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                        >
                                            <div style={{
                                                display: 'inline-block',
                                                backgroundColor: 'white',
                                                padding: '10px 12px',
                                                borderRadius: '10px',
                                                boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
                                                minWidth: '180px',
                                                position: 'relative'
                                            }}>
                                                <i
                                                    className="fa fa-close"
                                                    onClick={() => setActiveMarker(null)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '6px',
                                                        right: '8px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px',
                                                        color: '#999'
                                                    }}
                                                />

                                                <a target="_blank" href={`#/customer/${user._id}`} style={{ paddingRight: '15px' }}>
                                                    <h6 style={{
                                                        margin: 0,
                                                        fontSize: '16px',
                                                        fontWeight: 600
                                                    }}>
                                                        {capitalize(`${user.first_name} ${user.last_name}`)}
                                                    </h6>

                                                    <small style={{
                                                        fontSize: '14px',
                                                        color: '#666'
                                                    }}>
                                                        {user.mobile_number}
                                                    </small>
                                                </a>
                                            </div>
                                        </OverlayView>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </GoogleMap>
                </div>
            )}
        </>
    );
};

const Coverage = () => {

    const [openMap, setOpenMap] = useState(false);
    const [selectedZone, setSelectedZone] = useState(null);
    const [mainZone, setMainZone] = useState(null);

    const [filters, setFilters] = useState({
        active: true,
        hollow: true,
        credit: true
    });

    useEffect(() => {
        getZones().then((response) => {
            if(!response.error) {
                openZoneMarker(response.payload)
            }
        })
    }, []);

    const openZoneMarker = (zones) => {
        zoneMarker().then((result) => {
            if(!result.error) {
                data = result.payload;

                setMainZone({
                    name: zones[0].name,
                    coordinates: zones[0].geometry.coordinates[0].map(coord => ({ lat: coord[0], lng: coord[1] })),
                    fillColor: getRandomColor()
                });

                setSelectedZone(zones[0].subdivisions.map(item => ({
                    name: item.name,
                    coordinates: item.geometry.coordinates[0].map(coord => ({ lat: coord[0], lng: coord[1] })),
                    fillColor: getRandomColor()
                })));

                coordinate = zones[0].geometry.coordinates.flatMap(array => array.map(([lat, lng]) => ({ "lat": lat, "lng": lng })));
                setOpenMap(true);
            }
        })
    }

    const getRandomColor = () => {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    };

    const options = (type, checked) => {
        setFilters(prev => ({
            ...prev,
            [type]: checked
        }));
    };

    return (
        <div className="content-body">
            <div className="container-fluid pd-x-0">
                <div className="d-sm-flex align-items-center justify-content-between mg-b-20 mg-lg-b-25 mg-xl-b-30">
                    <div>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb breadcrumb-style1 mg-b-10">
                                <li className="breadcrumb-item active" aria-current="page">Coverage</li>
                            </ol>
                        </nav>
                        <h4 className="mg-b-0 tx-spacing--1">Coverage</h4>
                    </div>
                </div>

                <div className="row row-xs">
                    <div className="col-md-12 mg-t-10">
                        <div className="card">
                            <div className="card-header d-flex align-items-center justify-content-between">
                                <h6 className="mg-b-0">Zone Coverage</h6>
                                <div className="d-flex">
                                    <div className="custom-control custom-checkbox d-flex align-items-center">
                                        <input className="custom-control-input" type="checkbox"
                                               defaultChecked
                                               onClick={(event) => options("active", event.target.checked)}
                                               id="b1a"/>
                                        <label className="custom-control-label tx-14" htmlFor="b1a">Subscriber</label>
                                        <span className="ml-1 d-block wd-10 ht-10 bg-success rounded mg-r-5"></span>
                                    </div>

                                    <div className="custom-control custom-checkbox ml-3 d-flex align-items-center">
                                        <input className="custom-control-input" type="checkbox"
                                               defaultChecked
                                               onClick={(event) => options("hollow", event.target.checked)}
                                               id="b2a"/>
                                        <label className="custom-control-label tx-14" htmlFor="b2a">Unenrolled</label>
                                        <span className="ml-1 d-block wd-10 ht-10 bg-danger rounded mg-r-5"></span>
                                    </div>

                                    <div className="custom-control custom-checkbox ml-3 d-flex align-items-center">
                                        <input className="custom-control-input" type="checkbox"
                                               defaultChecked
                                               onClick={(event) => options("credit", event.target.checked)}
                                               id="b3a"/>
                                        <label className="custom-control-label tx-14" htmlFor="b3a">Credit</label>
                                        <span className="ml-1 d-block wd-10 ht-10 bg-warning rounded mg-r-5"></span>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                {!openMap ? (
                                    <h6>Query Coverage Data Map...</h6>
                                ) : (
                                    <GMap
                                        mainZone={mainZone}
                                        polygons={selectedZone}
                                        data={data}
                                        filters={filters}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Coverage;
